<template>
  <div v-if="show" class="report-layout-editor">
    <div class="popup-overlay" @click="cancel"></div>
    <div class="popup-content">
      <div class="popup-header">
        <h6 class="mb-0">{{ $t("reportColumnLayoutTitle") }}</h6>
        <button type="button" class="btn-close" @click="cancel" aria-label="Close">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="popup-body">
        <!-- Agrupar filas no se hace acá: se muestra lo activo para no confundir
             «Agrupar columnas» con el agrupado de filas del reporte. -->
        <div class="row-grouping-note small mb-2">
          <i class="fas fa-layer-group me-1" aria-hidden="true"></i>
          <template v-if="rowGroupingText">
            {{ $t("reportRowGroupingBy", { column: rowGroupingText }) }}
            <button
              type="button"
              class="btn btn-link btn-sm p-0 ms-1 align-baseline"
              @click="$emit('edit-row-grouping')"
            >
              {{ $t("reportRowGroupingChange") }}
            </button>
          </template>
          <template v-else>{{ $t("reportRowGroupingNone") }}</template>
        </div>
        <p class="text-muted small mb-2">{{ $t("reportColumnLayoutHint") }}</p>

        <div class="column-preview-list">
          <div
            v-for="col in previewColumns"
            :key="col.key"
            class="preview-item"
            :class="{ selected: selectedKeys.includes(col.key) }"
          >
            <input
              class="form-check-input"
              type="checkbox"
              :id="'layout-col-' + col.key"
              :value="col.key"
              v-model="selectedKeys"
            />
            <label class="form-check-label flex-grow-1" :for="'layout-col-' + col.key">
              <i
                v-if="col.isMerged"
                class="fas fa-compress-arrows-alt layout-merge-icon me-1"
                :title="$t('reportColumnMerge')"
                aria-hidden="true"
              ></i>
              {{ col.label }}
            </label>
            <span
              v-if="col.groupColor"
              class="group-color-dot"
              :style="{ backgroundColor: col.groupColor }"
              :title="col.groupTitle"
            ></span>
          </div>
        </div>

        <div class="layout-actions mt-3 d-flex flex-wrap gap-2">
          <button
            type="button"
            class="btn btn-sm btn-outline-primary"
            :disabled="selectedKeys.length < 2"
            @click="createGroup"
          >
            <i class="fas fa-object-group me-1"></i>{{ $t("reportColumnGroup") }}
          </button>
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary"
            :disabled="selectedKeys.length < 2"
            @click="createMerge"
          >
            <i class="fas fa-compress-arrows-alt me-1"></i>{{ $t("reportColumnMerge") }}
          </button>
        </div>

        <div v-if="tempLayout.merges.length" class="mt-3">
          <div class="small fw-semibold text-muted mb-1">{{ $t("reportColumnMergesList") }}</div>
          <div
            v-for="m in tempLayout.merges"
            :key="m.id"
            class="layout-config-item"
          >
            <span>
              <i
                class="fas fa-compress-arrows-alt layout-merge-icon me-1"
                :title="$t('reportColumnMerge')"
                aria-hidden="true"
              ></i>
              {{ mergeLabel(m) }}
              <span class="text-muted">({{ mergeModeLabel(m.mode) }})</span>
            </span>
            <button type="button" class="btn btn-link btn-sm text-danger p-0" @click="removeMerge(m.id)">
              <i class="fas fa-trash-alt"></i>
            </button>
          </div>
        </div>

        <div v-if="tempLayout.groups.length" class="mt-3">
          <div class="small fw-semibold text-muted mb-1">{{ $t("reportColumnGroupsList") }}</div>
          <div
            v-for="g in tempLayout.groups"
            :key="g.id"
            class="layout-config-item"
          >
            <span
              class="group-color-dot me-1"
              :style="{ backgroundColor: g.color }"
            ></span>
            <span>{{ g.title }} — {{ g.members.length }} {{ $t("reportColumnLayoutColumns") }}</span>
            <button type="button" class="btn btn-link btn-sm text-danger p-0" @click="removeGroup(g.id)">
              <i class="fas fa-trash-alt"></i>
            </button>
          </div>
        </div>
      </div>

      <div class="popup-footer">
        <button type="button" class="btn btn-outline-secondary btn-sm" @click="cancel">
          {{ $t("Cancel") }}
        </button>
        <button
          type="button"
          class="btn btn-primary btn-sm"
          :disabled="!canApplyLayout"
          @click="apply"
        >
          {{ $t("Apply") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from "sweetalert2";
import {
  REPORT_GROUP_COLORS,
  buildDisplayColumns,
  canSumMergedFields,
  mergeMemberKey,
  newLayoutId,
  normalizeColumnLayout,
} from "@/utils/reportColumnLayout";

export default {
  name: "ReportColumnLayoutEditor",
  props: {
    show: { type: Boolean, required: true },
    visibleFields: { type: Array, default: () => [] },
    layout: {
      type: Object,
      default: () => ({ groups: [], merges: [] }),
    },
    columnLabelResolver: { type: Function, default: null },
    /** Columna (y período) por la que se agrupan las filas, o "" si no se agrupan. */
    rowGroupingText: { type: String, default: "" },
  },
  data() {
    return {
      tempLayout: { groups: [], merges: [] },
      initialLayoutSnapshot: { groups: [], merges: [] },
      selectedKeys: [],
    };
  },
  computed: {
    canApplyLayout() {
      const current = normalizeColumnLayout(
        this.tempLayout,
        this.visibleFieldKeys
      );
      const hasGroupOrMerge =
        (current.merges && current.merges.length > 0) ||
        (current.groups && current.groups.length > 0);
      if (!hasGroupOrMerge) {
        return false;
      }
      return (
        JSON.stringify(current) !== JSON.stringify(this.initialLayoutSnapshot)
      );
    },
    visibleFieldKeys() {
      return (this.visibleFields || []).map((f) => f.key);
    },
    displayColumns() {
      return buildDisplayColumns(this.visibleFields, this.tempLayout);
    },
    previewColumns() {
      const keyToGroup = new Map();
      (this.tempLayout.groups || []).forEach((g) => {
        (g.members || []).forEach((k) => keyToGroup.set(k, g));
      });

      return this.displayColumns.map((col) => {
        const g = keyToGroup.get(col.key);
        let label = "";
        const isMerged = col.displayType === "merged";
        if (isMerged) {
          label =
            col.label ||
            col.fields
              .map((f) => this.resolveFieldLabel(f))
              .join(" / ");
        } else {
          label = this.resolveFieldLabel(col.field);
        }
        return {
          key: col.key,
          label,
          isMerged,
          groupTitle: g?.title || null,
          groupColor: g?.color || null,
        };
      });
    },
  },
  watch: {
    show(val) {
      if (val) {
        this.selectedKeys = [];
        const normalized = normalizeColumnLayout(
          JSON.parse(
            JSON.stringify(this.layout || { groups: [], merges: [] })
          ),
          this.visibleFieldKeys
        );
        this.tempLayout = normalized;
        this.initialLayoutSnapshot = JSON.parse(JSON.stringify(normalized));
      }
    },
  },
  methods: {
    resolveFieldLabel(field) {
      if (this.columnLabelResolver) {
        return this.columnLabelResolver(field);
      }
      return this.$t(field?.label || field?.key || "");
    },
    mergeLabel(merge) {
      const cols = buildDisplayColumns(this.visibleFields, {
        merges: [merge],
        groups: [],
      });
      const col = cols.find((c) => c.key === mergeMemberKey(merge.id));
      if (col?.displayType === "merged") {
        return col.fields.map((f) => this.resolveFieldLabel(f)).join(" + ");
      }
      return (merge.members || []).join(", ");
    },
    mergeModeLabel(mode) {
      return mode === "sum"
        ? this.$t("reportColumnMergeSum")
        : this.$t("reportColumnMergeStack");
    },
    sortedSelectedDisplayKeys() {
      const order = this.displayColumns.map((c) => c.key);
      return this.selectedKeys
        .filter((k) => order.includes(k))
        .sort((a, b) => order.indexOf(a) - order.indexOf(b));
    },
    fieldKeysFromDisplayKeys(displayKeys) {
      const keys = [];
      displayKeys.forEach((dk) => {
        const col = this.displayColumns.find((c) => c.key === dk);
        if (!col) return;
        if (col.displayType === "merged") {
          col.fields.forEach((f) => keys.push(f.key));
        } else {
          keys.push(col.field.key);
        }
      });
      return keys;
    },
    async createGroup() {
      const members = this.sortedSelectedDisplayKeys();
      if (members.length < 2) {
        Swal.fire({
          icon: "warning",
          title: this.$tn("reportColumnSelectTwo"),
        });
        return;
      }

      const colorOptions = REPORT_GROUP_COLORS.map(
        (c, i) =>
          `<button type="button" class="layout-color-pick" data-color="${c}" data-idx="${i}" style="background:${c}"></button>`
      ).join("");

      const { value: formValues, isConfirmed } = await Swal.fire({
        title: this.$tn("reportColumnGroup"),
        html: `
          <input id="swal-group-title" class="swal2-input" placeholder="${this.$tn("reportColumnGroupTitle")}" maxlength="80">
          <div class="layout-color-picker mt-2">${colorOptions}</div>
        `,
        focusConfirm: false,
        showCancelButton: true,
        cancelButtonText: this.$tn("Cancel"),
        confirmButtonText: this.$tn("Apply"),
        didOpen: () => {
          const popup = Swal.getPopup();
          popup.querySelectorAll(".layout-color-pick").forEach((btn) => {
            btn.addEventListener("click", () => {
              popup.querySelectorAll(".layout-color-pick").forEach((b) =>
                b.classList.remove("selected")
              );
              btn.classList.add("selected");
              popup.dataset.color = btn.dataset.color;
            });
          });
          const first = popup.querySelector(".layout-color-pick");
          if (first) {
            first.classList.add("selected");
            popup.dataset.color = first.dataset.color;
          }
        },
        preConfirm: () => {
          const title = document.getElementById("swal-group-title")?.value?.trim();
          if (!title) {
            Swal.showValidationMessage(this.$tn("reportColumnGroupTitleRequired"));
            return false;
          }
          const color =
            Swal.getPopup().dataset.color || REPORT_GROUP_COLORS[0];
          return { title, color };
        },
      });

      if (!isConfirmed || !formValues) return;

      const newGroup = {
        id: newLayoutId(),
        title: formValues.title,
        color: formValues.color,
        members: [...members],
      };

      const memberSet = new Set(members);
      this.tempLayout.groups = (this.tempLayout.groups || []).filter((g) => {
        const overlap = (g.members || []).some((m) => memberSet.has(m));
        return !overlap;
      });
      this.tempLayout.groups.push(newGroup);
      this.tempLayout = normalizeColumnLayout(
        this.tempLayout,
        this.visibleFieldKeys
      );
      this.selectedKeys = [];
    },
    isMergedDisplayKey(key) {
      return typeof key === "string" && key.startsWith("merge:");
    },
    async createMerge() {
      const displayKeys = this.sortedSelectedDisplayKeys();
      if (displayKeys.length < 2) {
        Swal.fire({
          icon: "warning",
          title: this.$tn("reportColumnSelectTwo"),
        });
        return;
      }

      if (displayKeys.some((k) => this.isMergedDisplayKey(k))) {
        Swal.fire({
          icon: "info",
          title: this.$tn("reportColumnMergeIncludesMergedTitle"),
          text: this.$tn("reportColumnMergeIncludesMergedText"),
        });
        return;
      }

      const fieldKeys = this.fieldKeysFromDisplayKeys(displayKeys);
      const fields = fieldKeys
        .map((k) => this.visibleFields.find((f) => f.key === k))
        .filter(Boolean);
      const canSum = canSumMergedFields(fields);

      const inputOptions = {
        stack: this.$t("reportColumnMergeStack"),
      };
      if (canSum) {
        inputOptions.sum = this.$t("reportColumnMergeSum");
      }

      const { value: mode, isConfirmed } = await Swal.fire({
        title: this.$tn("reportColumnMerge"),
        input: "radio",
        inputOptions,
        inputValue: "stack",
        showCancelButton: true,
        cancelButtonText: this.$tn("Cancel"),
        confirmButtonText: this.$tn("Apply"),
      });

      if (!isConfirmed || !mode) return;

      const overlapKeys = new Set(fieldKeys);
      this.tempLayout.merges = (this.tempLayout.merges || []).filter((m) => {
        const overlap = (m.members || []).some((k) => overlapKeys.has(k));
        return !overlap;
      });

      this.tempLayout.groups = (this.tempLayout.groups || []).map((g) => ({
        ...g,
        members: (g.members || []).filter((m) => !displayKeys.includes(m)),
      })).filter((g) => (g.members || []).length > 0);

      this.tempLayout.merges.push({
        id: newLayoutId(),
        members: fieldKeys,
        mode,
        label: "",
      });

      const normalized = normalizeColumnLayout(
        this.tempLayout,
        this.visibleFieldKeys
      );
      this.tempLayout = normalized;
      this.selectedKeys = [];
    },
    removeMerge(id) {
      this.tempLayout.merges = (this.tempLayout.merges || []).filter(
        (m) => m.id !== id
      );
      this.tempLayout = normalizeColumnLayout(
        this.tempLayout,
        this.visibleFieldKeys
      );
    },
    removeGroup(id) {
      this.tempLayout.groups = (this.tempLayout.groups || []).filter(
        (g) => g.id !== id
      );
      this.tempLayout = normalizeColumnLayout(
        this.tempLayout,
        this.visibleFieldKeys
      );
    },
    apply() {
      const normalized = normalizeColumnLayout(
        this.tempLayout,
        this.visibleFieldKeys
      );
      this.$emit("layout-changed", normalized);
      this.$emit("close");
    },
    cancel() {
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.row-grouping-note {
  padding: 0.4rem 0.6rem;
  border-radius: 6px;
  background: var(--bs-light, #f8f9fa);
  color: var(--bs-body-color, #212529);
}
.report-layout-editor {
  position: fixed;
  inset: 0;
  z-index: 1060;
}
.popup-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
}
.popup-content {
  position: absolute;
  top: 24px;
  right: 24px;
  width: 400px;
  max-height: 85vh;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
}
.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid #e9ecef;
}
.popup-body {
  padding: 12px 16px;
  overflow-y: auto;
  flex: 1;
}
.popup-footer {
  padding: 12px 16px;
  border-top: 1px solid #e9ecef;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}
.btn-close {
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
}
.column-preview-list {
  border: 1px solid #e9ecef;
  border-radius: 6px;
  max-height: 220px;
  overflow-y: auto;
}
.preview-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-bottom: 1px solid #f1f3f4;
  font-size: 0.85rem;
}
.preview-item.selected {
  background: #f0f9ff;
}
.preview-item:last-child {
  border-bottom: none;
}
.layout-merge-icon {
  font-size: 0.75rem;
  color: #6c757d;
  flex-shrink: 0;
}
.group-color-dot {
  width: 12px;
  height: 12px;
  border-radius: 2px;
  flex-shrink: 0;
  border: 1px solid rgba(0, 0, 0, 0.1);
}
.layout-config-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.8rem;
  padding: 4px 0;
}
</style>

<style>
.layout-color-picker {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
}
.layout-color-pick {
  width: 28px;
  height: 28px;
  border: 2px solid transparent;
  border-radius: 4px;
  cursor: pointer;
}
.layout-color-pick.selected {
  border-color: #0d6efd;
  box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.35);
}
</style>
